import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { API_BASE, setChangeContext } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { moneyCompact, money, num, perWatt, STATUS_LABELS, STATUS_TONE } from "@/lib/format";
import { ShowMath } from "@/components/ShowMath";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import ApprovalBanner from "@/components/ApprovalBanner";
import { FrozenBadge } from "@/components/FrozenBadge";
import ProjectChatButton from "@/components/messaging/ProjectChatButton";
import ScopeCompare from "@/components/ScopeCompare";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Send, Lock, GitBranch, Sun, FileSpreadsheet, Layers, History, ArrowDownToLine, ChevronUp, ChevronDown, TrendingUp, Copy, ListChecks, ArrowRight, AlertTriangle } from "lucide-react";
import { InBidSearch } from "@/components/InBidSearch";
import { KeyDatesStrip } from "@/components/KeyDatesStrip";
import { SignoffChip, RocketFuelLink, fmtDay } from "@/components/BidOpsChips";
import ImportedBidView from "@/components/ImportedBidView";
import ClientLinkPanel from "@/components/ClientLinkPanel";
import { BidTeamChip } from "@/components/BidTeam";
import { RoundCompareButton } from "@/components/RoundCompare";

import TakeoffTab from "@/pages/tabs/TakeoffTab";
import DocumentsTab from "@/pages/tabs/DocumentsTab";
import ProposalTab from "@/pages/tabs/ProposalTab";
import PricingMath from "@/pages/tabs/PricingMath";
import LevelingTab from "@/pages/tabs/LevelingTab";
import LocationPanel from "@/pages/tabs/LocationPanel";
import EstimatingTab from "@/pages/tabs/EstimatingTab";
import ScheduleTab from "@/pages/tabs/ScheduleTab";
import ManagementTab from "@/pages/tabs/ManagementTab";
import GeneralConditions from "@/pages/tabs/GeneralConditions";
import MobilizationTab from "@/pages/tabs/MobilizationTab";
import CommercialTermsTab from "@/pages/tabs/CommercialTermsTab";
import AllowancesTab from "@/pages/tabs/AllowancesTab";
import OptionsTab from "@/pages/tabs/OptionsTab";
import ContractTab from "@/pages/tabs/ContractTab";
import ContextTab from "@/pages/tabs/ContextTab";
import BidChecklist from "@/components/BidChecklist";
import { VersionTabs } from "@/components/VersionTabs";
import VersionView from "@/components/VersionView";
import ActivityPanel from "@/components/ActivityPanel";
import { useClaudeChanges, ClaudeChip } from "@/components/claude/ClaudeChanges";
import { DraftsCard } from "@/components/claude/EmailDrafts";
import { NdaBanner, NdaOverrideDialog, ndaGateOf } from "@/components/nda/NdaBits";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

// Priced-elsewhere / imported bids: the record view plus Context (project memory + email log) and Documents — pricing tabs hidden.
function ExternalBidShell({ bid, bidId, sp, canTakeoff, canDelete, canSend, onChange }) {
  const [xtab, setXtab] = useState(sp.get("tab") === "context" || sp.get("tab") === "documents" ? sp.get("tab") : "record");
  const [signoff, setSignoff] = useState(null);
  const [sending, setSending] = useState(false);
  const tabs = [["record", "Record"], ["context", "Context"], ["documents", "Documents"]];
  const loadSignoff = useCallback(() => { api.get(`/bids/${bidId}/signoff-state`).then((r) => setSignoff(r.data)).catch(() => {}); }, [bidId]);
  useEffect(() => { loadSignoff(); }, [loadSignoff, bid.status, bid.approval_status, bid.updated_at]);
  const sendLock = signoff?.required && signoff.state !== "approved" && signoff.state !== "sent"
    ? ({ pending: `Waiting on ${(signoff.waiting_on || []).join(", ") || "the approver"}'s sign-off`, stale: "The proposal changed — new sign-off needed", rejected: "Changes requested — resubmit" }[signoff.state] || "Needs sign-off before it can be sent")
    : null;
  const send = async () => {
    if (!window.confirm("Mark this bid sent at its lump sum, and release the proposal to the client link?")) return;
    setSending(true);
    try { const r = await api.post(`/bids/${bidId}/send`, {}); toast.success(`Marked sent (round ${r.data.revision})`); onChange && onChange(); loadSignoff(); }
    catch (e) { const d = e.response?.data?.detail; toast.error(d?.message || d || "Could not send"); }
    finally { setSending(false); }
  };
  return (
    <div className="max-w-[1800px] w-full mx-auto px-4 sm:px-6 py-4" data-testid="external-bid-shell">
      {!bid.read_only && bid.external_priced && (
        <div className="mb-3 rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="external-review-bar">
          <ApprovalBanner bidId={bidId} signoff={signoff} onChange={() => { onChange && onChange(); loadSignoff(); }} />
          {bid.status !== "sent" && (
            <div className="px-5 py-2 flex items-center justify-between gap-3 flex-wrap text-xs text-slate-600">
              <span data-testid="external-stage-note">{bid.proposal_source === "provided" ? `Proposal on file: ${bid.proposal_provided?.filename || "PDF"}` : "No proposal PDF attached yet (Documents → upload, then Use as proposal)."}{sendLock ? ` · ${sendLock}` : ""}</span>
              {canSend && (
                <Button size="sm" onClick={send} disabled={sending || !!sendLock} title={sendLock || ""} className="h-7 bg-[#003E87] hover:bg-[#00305F] text-white" data-testid="external-send-btn">
                  <Send className="w-3 h-3 mr-1" /> {sending ? "…" : "Mark sent & release"}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
      <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
        {tabs.map(([k, l]) => (
          <button key={k} data-testid={`external-tab-${k}`} onClick={() => setXtab(k)}
            className={`px-3 py-1.5 text-sm rounded-md ${xtab === k ? "bg-white shadow-sm font-semibold text-[#003E87]" : "text-slate-600 hover:text-slate-900"}`}>{l}</button>
        ))}
      </div>
      <BidTeamChip bidId={bidId} />
      <RoundCompareButton bidId={bidId} refreshKey={`${bid.revision}-${bid.status}-${bid.round_label}`} />
      </div>
      {xtab === "record" && (<><ImportedBidView bid={bid} />{!bid.read_only && <ClientLinkPanel bid={bid} canManage={canSend} onReleased={onChange} />}</>)}
      {xtab === "context" && <ContextTab bidId={bidId} autoReply={sp.get("reply") === "1"} />}
      {xtab === "documents" && <DocumentsTab bid={bid} readOnly={!canTakeoff} canDelete={canDelete} onOpenSheet={() => {}} />}
    </div>
  );
}

const TABS = [
  { key: "context", label: "Context", testid: "bid-workspace-tab-context" },
  { key: "documents", label: "Documents", testid: "bid-workspace-tab-documents" },
  { key: "takeoff", label: "Takeoff", testid: "bid-workspace-tab-drawings" },
  { key: "location", label: "Productivity", testid: "bid-workspace-tab-location" },
  { key: "commercial_terms", label: "Commercial Terms", testid: "bid-workspace-tab-commercial-terms" },
  { key: "estimating", label: "Estimating", testid: "bid-workspace-tab-estimating" },
  { key: "schedule", label: "Schedule", testid: "bid-workspace-tab-schedule" },
  { key: "leveling", label: "Bid Leveling", testid: "bid-workspace-tab-leveling" },
  { key: "management", label: "Management", testid: "bid-workspace-tab-management" },
  { key: "conditions", label: "General Conditions", testid: "bid-workspace-tab-conditions" },
  { key: "mobilization", label: "Mobilization", testid: "bid-workspace-tab-mobilization" },
  { key: "allowances", label: "Allowances & Alternates", testid: "bid-workspace-tab-allowances" },
  { key: "options", label: "Options & Pay Items", testid: "bid-workspace-tab-options" },
  { key: "rollup", label: "Rollup", testid: "bid-workspace-tab-rollup" },
  { key: "proposal", label: "Proposal", testid: "bid-workspace-tab-proposal" },
  { key: "contract", label: "Contract", testid: "bid-workspace-tab-contract" },
];

// Grouped tab IA — clusters the 13 tabs by estimating stage for clarity.
const TAB_GROUPS = [
  { key: "crm", label: "Client & Context", keys: ["context"] },
  { key: "scope", label: "Scope & Design", keys: ["documents", "takeoff", "location"] },
  { key: "directs", label: "Cost & Directs", keys: ["commercial_terms", "estimating", "schedule", "leveling"] },
  { key: "overhead", label: "Overhead & Alternates", keys: ["management", "conditions", "mobilization", "allowances", "options"] },
  { key: "output", label: "Rollup & Output", keys: ["rollup", "proposal", "contract"] },
];

export function perms(role) {
  return {
    canPrice: ["owner", "admin", "chief_estimator", "estimator"].includes(role),
    canTakeoff: ["owner", "admin", "chief_estimator", "estimator"].includes(role),
    canSetMargin: ["owner", "admin", "chief_estimator"].includes(role),
    canSend: ["owner", "admin", "chief_estimator"].includes(role),
    canDelete: ["owner", "admin", "chief_estimator"].includes(role),
  };
}

export default function BidWorkspace() {
  const { bidId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const p = perms(user?.role);

  const [bid, setBid] = useState(null);
  const [nda, setNda] = useState(null);
  const [ndaGate, setNdaGate] = useState(null);
  const [computed, setComputed] = useState(null);
  const [repricePreview, setRepricePreview] = useState(null);
  const [totalFlash, setTotalFlash] = useState(false);
  const lastTotalRef = useRef(null);
  const [taskTypes, setTaskTypes] = useState([]);
  const [crews, setCrews] = useState([]);
  const [tab, setTab] = useState("takeoff");
  const [followOpen, setFollowOpen] = useState(false);
  const [chromeOpen, setChromeOpen] = useState(() => {
    const s = localStorage.getItem("bidChromeOpen");
    if (s === "0") return false;
    if (s === "1") return true;
    // No saved preference: default collapsed on short laptop screens so the estimate isn't buried.
    return typeof window !== "undefined" ? window.innerHeight >= 860 : true;
  });
  const [takeoffFull, setTakeoffFull] = useState(false);
  const fullRef = useRef(null);
  const [openDrawingId, setOpenDrawingId] = useState(null);
  const [locateTerm, setLocateTerm] = useState(null);
  const [flashMarkupId, setFlashMarkupId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [histFilter, setHistFilter] = useState("all");
  const [showRevs, setShowRevs] = useState(false);
  const [revs, setRevs] = useState([]);
  const [revDiff, setRevDiff] = useState(null);
  const [selRev, setSelRev] = useState(null);
  const [compareTo, setCompareTo] = useState("current");
  const fetchRevDiff = async (aId, toId) => {
    setRevDiff({ loading: true });
    try {
      const params = { revision_id: aId };
      if (toId && toId !== "current") params.to_revision_id = toId;
      const r = await api.get(`/bids/${bid.bid_id}/revision-diff`, { params });
      setRevDiff({ ...r.data, revision_id: aId, to_id: toId });
    } catch { setRevDiff(null); }
  };
  const fmtMoney = (n) => (n == null ? "—" : "$" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }));
  const [tplOpen, setTplOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [tplCat, setTplCat] = useState("full_epc");
  const openSaveTemplate = () => { setTplName(bid?.name ? `${bid.name} template` : "Project template"); setTplCat("full_epc"); setTplOpen(true); };
  const submitTemplate = async () => {
    if (!tplName.trim()) { toast.error("Name the template"); return; }
    try { await api.post(`/bids/${bidId}/save-as-template`, { name: tplName.trim(), category: tplCat }); toast.success(`Saved “${tplName.trim()}” as a project template`); setTplOpen(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save template"); }
  };
  const [sp] = useSearchParams();
  // GF 2.0 option mode: ?option=opt_x edits that option's estimate; fields you change become its own.
  const optionId = sp.get("option") || null;
  const optQ = optionId ? `?option=${encodeURIComponent(optionId)}` : "";
  const openSheetInTakeoff = useCallback(async (sheet, term) => {
    try {
      const res = await api.post(`/bids/${bidId}/sheets/${sheet.sheet_id}/to-takeoff`);
      setBid((b) => {
        const exists = (b.drawings || []).some((d) => d.drawing_id === res.data.drawing_id);
        return { ...b, drawings: exists ? b.drawings : [...(b.drawings || []), res.data] };
      });
      setOpenDrawingId(res.data.drawing_id);
      setLocateTerm(term || null);
      setTab("takeoff");
    } catch (e) {
      toast.error("Could not open that sheet");
    }
  }, [bidId]);
  const [versions, setVersions] = useState([]);
  const [activeVersionId, setActiveVersionId] = useState(null);
  const [viewingId, setViewingId] = useState(null);
  const [viewSnap, setViewSnap] = useState(null);
  const [idxList, setIdxList] = useState([]);
  const [cmdtyDismissed, setCmdtyDismissed] = useState(false);
  useEffect(() => { api.get("/commodity-indices").then((r) => setIdxList(r.data || [])).catch(() => {}); }, []);
  const [signoff, setSignoff] = useState(null);
  const loadSignoff = useCallback(() => {
    api.get(`/bids/${bidId}/signoff-state`).then((r) => setSignoff(r.data)).catch(() => {});
  }, [bidId]);
  const liveTotal = computed?.totals?.total;
  // Re-check after edits/price moves (debounced) so a post-approval price change re-locks Send.
  useEffect(() => {
    const t = setTimeout(loadSignoff, 600);
    return () => clearTimeout(t);
  }, [loadSignoff, bid?.updated_at, bid?.status, liveTotal]);
  // Flag when a linked commodity has drifted >=5% from the price frozen onto this bid's lines.
  const commodityAlerts = useMemo(() => {
    const lines = bid?.scope_lines || [];
    if (!lines.length || !idxList.length) return [];
    const map = Object.fromEntries(idxList.map((i) => [i.index_id, i]));
    const worst = {};
    const bump = (iid, freezePrice) => {
      const idx = map[iid];
      const fp = Number(freezePrice) || 0;
      const cur = Number(idx?.current_price) || 0;
      if (!idx || fp <= 0 || cur <= 0) return;
      const drift = (cur - fp) / fp;
      if (Math.abs(drift) >= 0.05 && Math.abs(drift) > Math.abs(worst[iid]?.drift || 0)) worst[iid] = { name: idx.name, drift };
    };
    for (const l of lines) {
      if (l.commodity_index_id && Number(l.commodity_base_material)) {
        bump(l.commodity_index_id, Number(l.commodity_baseline_price) * Number(l.unit_material_cost) / Number(l.commodity_base_material));
      }
      (l.commodity_components || []).forEach((c) => bump(c.index_id, Number(c.baseline_price)));
    }
    return Object.values(worst);
  }, [bid?.scope_lines, idxList]);
  const loadVersions = useCallback(async () => {
    if (!bidId) return;
    try {
      const r = await api.get(`/bids/${bidId}/versions`);
      setVersions(r.data.versions || []);
      setActiveVersionId(r.data.active_version_id);
      setViewingId((cur) => (cur && (r.data.versions || []).some((v) => v.version_id === cur) ? cur : r.data.active_version_id));
    } catch { /* versions are additive; ignore */ }
  }, [bidId]);
  useEffect(() => { loadVersions(); }, [loadVersions]);
  const claude = useClaudeChanges(bidId);
  // keep the active tab's total in step with live recompute (no extra network call)
  useEffect(() => {
    if (!activeVersionId) return;
    setVersions((vs) => vs.map((v) => (v.version_id === activeVersionId ? { ...v, total: computed?.totals?.total ?? v.total } : v)));
  }, [computed?.totals?.total, activeVersionId]);

  const selectVersion = async (v) => {
    if (v.version_id === activeVersionId) { setViewingId(activeVersionId); setViewSnap(null); return; }
    setViewingId(v.version_id);
    try { const r = await api.get(`/bids/${bidId}/versions/${v.version_id}`); setViewSnap(r.data); }
    catch { setViewSnap(null); toast.error("Could not open that version"); }
  };
  const createVersion = async (type) => {
    let extra = {};
    // H: Procore-style — Copy from an earlier version or Start blank; a change order asks for the client's request
    if (type === "revision" || type === "alternate") {
      const elsewhere = bid?.external_priced || bid?.imported;
      const opts = versions.map((v, i) => `${i + 1} = copy from ${v.label}${v.sent_at ? " (sent)" : ""}`).join(", ");
      const dflt = String(Math.max(1, versions.findIndex((v) => v.sent_at) + 1 || versions.length));
      const pick = window.prompt(`${type === "alternate" ? "Alternate" : "New revision"} — ${opts}, or 0 = start blank (bid setup only)${elsewhere ? ", or H = Price in house (fresh GridForge estimate, engine on; the sent rounds keep their lump sums)" : ""}`, elsewhere ? "H" : dflt);
      if (pick === null) return;
      if (/^h/i.test(pick.trim()) && elsewhere) extra.mode = "in_house";
      else {
        const n = parseInt(pick, 10);
        extra.copy_from = n === 0 ? "blank" : (versions[n - 1]?.version_id || null);
      }
    }
    if (type === "change_order") {
      const text = window.prompt("Change order — what did the client ask for (added or deducted scope)?", "");
      if (text === null) return;
      const asked_by = window.prompt("Who asked (name)?", "") || null;
      const date = window.prompt("Date of the request, YYYY-MM-DD", new Date().toISOString().slice(0, 10)) || null;
      extra.co_request = { text, asked_by, date };
    }
    {
      // C: each round has its own dates — the Original's are shown for reference, blank means "no date given"
      const ref = `(Original: client due ${bid?.due_date || "—"}, internal due ${bid?.internal_due_date || "—"})`;
      const cd = window.prompt(`Client due date for THIS ${type === "change_order" ? "change order" : "version"}, YYYY-MM-DD or blank ${ref}`, "");
      if (cd === null) return;
      const idd = window.prompt("Internal due date for this version, YYYY-MM-DD or blank", "");
      if (idd === null) return;
      const reason = window.prompt("Reason (goes to the bid activity)", "") || "";
      extra = { ...extra, client_due_date: cd.trim() || null, internal_due_date: idd.trim() || null, reason };
    }
    try {
      const r = await api.post(`/bids/${bidId}/versions`, { type, ...extra });
      setViewSnap(null);
      setVersions(r.data.versions || []); setActiveVersionId(r.data.active_version_id); setViewingId(r.data.active_version_id);
      toast.success(extra.mode === "in_house" ? "In-house revision opened — the engine is on; the sent rounds keep their lump sums" : "New version created — edit it freely");
      await load();
      setTab("estimating");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create version"); }
  };
  useEffect(() => {
    if (!bid || sp.get("revise") !== "1") return;
    if (bid.status === "sent" || bid.status === "won") {
      createVersion("revision");
      navigate(`/bids/${bidId}`, { replace: true });
    }
  }, [bid?.bid_id, bid?.status]);  // eslint-disable-line react-hooks/exhaustive-deps
  const deleteVersion = async (v) => {
    if (v.protected) { toast.error(v.protected); return; }
    const what = `Delete ${v.label}?\n\nThis removes the tab and its draft lines, groups, calculators and pay items (${v.total != null ? `$${Math.round(v.total).toLocaleString()}` : "no total"}). It was never sent, carries no approval or stamps and has no documents of its own.\n\nA restore point is taken first — Undo (undo_backfill) can bring it back within 30 days. The deletion is logged on the bid.`;
    if (!window.confirm(what)) return;
    const reason = window.prompt("Why is it being deleted? (goes to the activity)", "no longer useful");
    if (reason === null) return;
    try {
      const r = await api.post(`/bids/${bidId}/versions/${v.version_id}/delete`, { reason });
      setViewSnap(null);
      setVersions(r.data.versions || []); setActiveVersionId(r.data.active_version_id); setViewingId(r.data.active_version_id);
      toast.success(`Deleted ${v.label} — restore point ${r.data.deleted?.restore_point}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete version"); }
  };
  const activeLabel = versions.find((v) => v.version_id === activeVersionId)?.label || "current";
  // H: the one-line why box — rides on every write to this bid; cleared when the bid changes
  const [whyNote, setWhyNote] = useState("");
  useEffect(() => { setChangeContext({ bidId, reason: whyNote, by: user?.name || user?.email || "" }); return () => setChangeContext({ bidId: null, reason: "" }); }, [bidId, whyNote, user?.name, user?.email]);
  // deep link from Compare: ?version=<id> opens that version, &line=<id> scrolls to and flashes the line
  useEffect(() => {
    const vid = sp.get("version");
    if (!vid || !versions.length) return;
    const v = versions.find((x) => x.version_id === vid);
    if (v && vid !== viewingId) selectVersion(v);
    const lid = sp.get("line") || sp.get("group");
    if (!lid) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-line-id="${CSS.escape(lid)}"], [data-group-name="${CSS.escape(lid)}"]`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-2", "ring-[#F4C400]"); setTimeout(() => el.classList.remove("ring-2", "ring-[#F4C400]"), 4000); }
    }, 900);
    return () => clearTimeout(t);
  }, [sp.get("version"), sp.get("line"), versions.length, viewSnap?.version_id, tab]);  // eslint-disable-line react-hooks/exhaustive-deps
  const [pendingMeasure, setPendingMeasure] = useState(null);
  const [checklist, setChecklist] = useState(null);
  useEffect(() => {
    if (!bidId) return;
    api.get(`/bids/${bidId}/checklist`).then((r) => setChecklist(r.data)).catch(() => {});
  }, [bidId, tab, computed?.totals?.total, bid?.status, bid?.scope_lines?.length, bid?.drawings?.length]);

  const recompute = useCallback(async () => {
    const r = await api.post(`/bids/${bidId}/compute${optQ}`);
    setComputed(r.data);
  }, [bidId, optQ]);

  // Always-in-sync safety net: whenever anything price-relevant on the bid changes — through ANY
  // path (takeoff edits, sheet scale, assembly overrides, grid edits, reorders) — re-pull the
  // authoritative computed totals so the header TOTAL never drifts from the live estimate. The old
  // design only recomputed inside updateBid, so paths that setBid directly left the total stale
  // until a remount ("refreshes when I leave and come back"). Debounced to coalesce rapid edits.
  const bidSig = useMemo(() => {
    if (!bid) return "";
    const { markups, ...rest } = bid;   // markups flow into scope_lines via self-heal; skip their bulk
    try { return JSON.stringify(rest); } catch { return ""; }
  }, [bid]);
  const sigRef = useRef(null);
  useEffect(() => {
    if (!bid || !bidSig) return;
    if (sigRef.current === null) { sigRef.current = bidSig; return; }  // initial load() already computed
    if (sigRef.current === bidSig) return;
    sigRef.current = bidSig;
    const t = setTimeout(() => { recompute().catch(() => {}); }, 150);
    return () => clearTimeout(t);
  }, [bidSig, bid, recompute]);

  // Subtle flash on the header TOTAL whenever it changes, so estimators get instant visual confirmation
  // their edit moved the price (pairs with the always-in-sync recompute above).
  useEffect(() => {
    const tot = computed?.totals?.total;
    if (tot == null) return;
    if (lastTotalRef.current !== null && lastTotalRef.current !== tot) {
      setTotalFlash(true);
      const h = setTimeout(() => setTotalFlash(false), 700);
      lastTotalRef.current = tot;
      return () => clearTimeout(h);
    }
    lastTotalRef.current = tot;
  }, [computed?.totals?.total]);

  const load = useCallback(async () => {
    const [b, tt, cr] = await Promise.all([
      api.get(optionId ? `/bids/${bidId}/options/${optionId}/view` : `/bids/${bidId}`), api.get("/task-types"), api.get("/crew-templates"),
    ]);
    setBid(b.data);
    api.get(`/bids/${bidId}/nda`).then((r) => setNda(r.data)).catch(() => setNda(null));
    setTaskTypes(tt.data);
    setCrews(cr.data);
    const c = await api.post(`/bids/${bidId}/compute${optQ}`);
    setComputed(c.data);
  }, [bidId, optionId, optQ]);

  useEffect(() => { load(); }, [load]);

  // TRUE full screen: once the portaled overlay mounts, hand it to the browser's
  // Fullscreen API so the browser itself goes full screen (OS/browser chrome collapses,
  // exactly like YouTube). The overlay is portaled to <body>, so the browser paints
  // ONLY the drawing + its tools — the global GridForge nav is gone entirely.
  useEffect(() => {
    if (takeoffFull && tab === "takeoff" && fullRef.current && !document.fullscreenElement) {
      fullRef.current.requestFullscreen?.().catch(() => {});
    }
  }, [takeoffFull, tab]);
  // Keep state in step when the user leaves full screen via Esc or the browser UI.
  useEffect(() => {
    const onFs = () => { if (!document.fullscreenElement) setTakeoffFull(false); };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);
  const exitTakeoffFull = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    setTakeoffFull(false);
  }, []);

  // Remember where this user is inside the bid (tab + open drawing) so Home can offer a
  // "pick up where you left off" suggestion that lands back on this exact page/drawing.
  useEffect(() => {
    if (!bid?.bid_id || bid.read_only || bid.external_priced) return;
    const tid = setTimeout(() => {
      const onTakeoff = tab === "takeoff";
      const drawing = onTakeoff && openDrawingId ? (bid.drawings || []).find((d) => d.drawing_id === openDrawingId) : null;
      const tabLabel = (TABS.find((t) => t.key === tab) || {}).label || tab;
      const label = drawing ? `Takeoff · ${drawing.name}` : tabLabel;
      api.post(`/bids/${bid.bid_id}/position`, {
        tab, drawing_id: drawing ? drawing.drawing_id : null, drawing_name: drawing?.name || null, label,
      }).catch(() => {});
    }, 1200);
    return () => clearTimeout(tid);
  }, [bid?.bid_id, bid?.read_only, bid?.external_priced, tab, openDrawingId]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!bid?.bid_id) return;
    const t = sp.get("tab");
    if (t) setTab(t);
    else if (bid.read_only) setTab("rollup");
    const sid = sp.get("sheet");
    if (sid) openSheetInTakeoff({ sheet_id: sid }, sp.get("q") || "");
    const did = sp.get("drawing");
    if (did && !sid) { setOpenDrawingId(did); setTab("takeoff"); }
    setFlashMarkupId(sp.get("markup") || null);
  }, [bid?.bid_id, sp.get("sheet"), sp.get("q"), sp.get("tab"), sp.get("markup"), sp.get("drawing")]); // eslint-disable-line react-hooks/exhaustive-deps

  const locked = bid?.status === "sent";
  const activeV = versions.find((v) => v.version_id === activeVersionId);
  const isRevision = !!activeV && (activeV.seq || 1) > 1 && !locked && p.canPrice;
  const repriceIndex = async () => {
    try {
      const r = await api.post(`/bids/${bidId}/reprice-index?preview=true`);
      if ((r.data.repriced || 0) > 0) setRepricePreview({ changes: r.data.changes || [], total: r.data.total_delta || 0 });
      else toast.info("No index-linked lines to reprice on this revision");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reprice"); }
  };
  const applyReprice = async () => {
    try {
      const r = await api.post(`/bids/${bidId}/reprice-index`);
      toast.success(`Repriced ${r.data.repriced} line${r.data.repriced === 1 ? "" : "s"} to current index rates`);
      setRepricePreview(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reprice"); }
  };

  const updateBid = async (patch) => {
    try {
      if (optionId) {
        await api.put(`/bids/${bidId}/options/${optionId}`, { set: patch });
        const v = await api.get(`/bids/${bidId}/options/${optionId}/view`);
        setBid(v.data);
        return v.data;
      }
      const r = await api.put(`/bids/${bidId}`, patch);
      setBid(r.data);
      return r.data;   // totals re-sync automatically via the bidSig watcher below (covers every path)
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "frozen" && !patch._void_approval) {
        if (window.confirm(d.message)) return updateBid({ ...patch, _void_approval: true });
        throw e;
      }
      toast.error(typeof d === "object" ? d?.message || "Could not save" : d || "Could not save");
      throw e;
    }
  };

  const sendBid = async (confirm = false, ndaReason = null) => {
    try {
      const body = { ...(confirm ? { confirm_mismatch: true } : {}), ...(ndaReason ? { nda_override_reason: ndaReason } : {}) };
      const r = await api.post(`/bids/${bidId}/send`, Object.keys(body).length ? body : undefined);
      toast.success(`Sent — revision ${r.data.revision}, ${moneyCompact(r.data.total)}`);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "proposal_total_mismatch" && !confirm) {
        if (window.confirm(d.message)) return sendBid(true, ndaReason);
        return;
      }
      const gate = ndaGateOf(e);
      if (gate) { setNdaGate({ ...gate, retry: (reason) => sendBid(confirm, reason) }); return; }
      toast.error((typeof d === "string" ? d : d?.message) || "Could not send");
    }
  };

  const addScopeFromMeasurement = (m) => {
    setPendingMeasure(m);
    setTab("estimating");
    toast.info(`Added ${m.value} ${m.unit} to the estimate grid.`);
  };

  if (!bid || !computed) {
    return <div className="min-h-full flex items-center justify-center"><Sun className="w-6 h-6 animate-spin text-blue-600" /></div>;
  }

  // Imported / historical "shadow" bids AND priced-elsewhere lump-sum bids get a slim record
  // view (not the confusing full 13-tab estimating workspace, which has no live scope for them).
  if (bid.read_only || bid.external_priced) {
    return <ExternalBidShell bid={bid} bidId={bidId} sp={sp} canTakeoff={p.canTakeoff} canDelete={p.canDelete} canSend={p.canSend} onChange={load} />;
  }

  const t = computed.totals;
  const needsDiscipline = !bid.read_only && !bid.is_historical &&
    (!bid.discipline || !bid.scope_completeness ||
     (bid.scope_completeness === "partial" && !bid.scope_completeness_note));

  const toggleChrome = () => setChromeOpen((v) => { localStorage.setItem("bidChromeOpen", v ? "0" : "1"); return !v; });
  const SEND_LOCK = { pending: () => `Waiting on ${(signoff.waiting_on || []).join(", ") || "the approver"}'s sign-off`,
    stale: () => "Price changed — new sign-off needed", rejected: () => "Edits requested — resubmit",
    none: () => (signoff.bdm_mode ? "Needs BDM sign-off" : "Needs sign-off") };
  const sendLock = signoff?.required && SEND_LOCK[signoff.state] ? SEND_LOCK[signoff.state]() : null;

  const takeoffTabEl = (
    <TakeoffTab bid={bid} setBid={setBid} readOnly={locked || !p.canTakeoff} onSendToEstimate={addScopeFromMeasurement} updateBid={updateBid} recompute={recompute} openDrawingId={openDrawingId} locateTerm={locateTerm} flashMarkupId={flashMarkupId} computed={computed} taskTypes={taskTypes} onGoToEstimate={() => setTab("estimating")} onActiveDrawing={(d) => { if (d?.drawing_id) setOpenDrawingId(d.drawing_id); }}
      fullscreen={takeoffFull} onToggleFullscreen={() => (takeoffFull ? exitTakeoffFull() : setTakeoffFull(true))} />
  );

  // Full-screen takeoff renders the SAME TakeoffTab instance in the same tree slot (see the
  // takeoff tab below) — only its wrapper class toggles + the browser Fullscreen API is handed
  // the wrapper. Keeping one mounted instance preserves the open drawing and collapsed groups
  // across enter/exit (a portal/early-return here used to remount and reset both).
  return (
    <div className="flex flex-col h-full">
      {/* Top bar — collapsed: one thin strip (back · name · status · tabs · total · expand) */}
      {!chromeOpen && (
        <div className="shrink-0 bg-white border-b border-slate-200" data-testid="bid-chrome-strip">
        <div className="flex items-center gap-2 px-4 h-11">
          <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-slate-700 shrink-0" data-testid="bid-back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <span className="font-semibold text-[15px] text-slate-900 truncate min-w-0" title={bid.name} data-testid="bid-strip-name">{bid.name}</span>
          <Badge className={`${STATUS_TONE[bid.status]} hover:${STATUS_TONE[bid.status]} h-5 px-1.5 text-[11px] shrink-0`}>{STATUS_LABELS[bid.status]}</Badge>
          <FrozenBadge bid={bid} compact testid="bid-frozen-badge-strip" />
          <ClaudeChip bidId={bidId} state={claude} onRestored={load} />
          <BidTeamChip bidId={bidId} compact />
          <RoundCompareButton bidId={bidId} refreshKey={`${bid.revision}-${bid.status}`} />
          <div className="flex-1" />
          {sendLock && <span className="hidden xl:inline-flex items-center gap-1 text-[11px] text-slate-500 shrink-0 max-w-[260px] truncate" title={sendLock}><Lock className="w-3 h-3 shrink-0" /> {sendLock}</span>}
          <span className="text-[11px] uppercase tracking-wide text-slate-400 shrink-0">Total</span>
          <span className="font-mono tabular-nums text-[15px] font-semibold text-slate-900 shrink-0" data-testid="bid-total-block">
            {bid.read_only ? money(bid.stored_total) : t ? money(t.total) : "—"}
          </span>
          <button onClick={toggleChrome} data-testid="bid-chrome-toggle" title="Show the full bid header"
            className="flex items-center gap-1 h-7 px-2 text-[11px] text-slate-500 hover:text-slate-800 rounded-md hover:bg-slate-100 whitespace-nowrap shrink-0">
            <ChevronDown className="w-3.5 h-3.5" /> Details
          </button>
        </div>
          <div className="flex items-center px-3 overflow-x-auto whitespace-nowrap border-t border-slate-100" style={{ scrollbarWidth: "thin" }} data-testid="bid-tab-groups">
            {TABS.map((tb) => (
              <button key={tb.key} data-testid={tb.testid} onClick={() => setTab(tb.key)}
                className={`px-2.5 h-9 text-[13px] whitespace-nowrap border-b-2 transition-colors shrink-0 ${tab === tb.key ? "border-[#003E87] text-[#003E87] font-semibold" : "border-transparent text-slate-600 hover:text-slate-900"}`}>
                {tb.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {chromeOpen && (
      <div className="shrink-0 bg-white border-b border-slate-200">
        <div className={`flex items-center gap-4 px-5 ${chromeOpen ? "py-3" : "py-1.5"}`}>
          <button onClick={() => navigate(-1)} className="text-slate-400 hover:text-slate-700" data-testid="bid-back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-lg font-bold tracking-tight text-slate-900 truncate">{bid.name}</h1>
              <Badge className={`${STATUS_TONE[bid.status]} hover:${STATUS_TONE[bid.status]}`}>{STATUS_LABELS[bid.status]}</Badge>
              <FrozenBadge bid={bid} testid="bid-frozen-badge" />
              {bid.revision ? <span className="text-xs text-slate-400 font-mono">rev {bid.revision}</span> : null}
              {signoff?.state === "pending" && <SignoffChip names={signoff.waiting_on} testid="bid-signoff-waiting" />}
              <RocketFuelLink url={signoff?.summary?.rocketfuel_url} label testid="bid-rocketfuel-link" />
              <ClaudeChip bidId={bidId} state={claude} onRestored={load} />
              <BidTeamChip bidId={bidId} />
              <RoundCompareButton bidId={bidId} refreshKey={`${bid.revision}-${bid.status}`} />
            </div>
            <div className="text-xs text-slate-500 truncate">
              {bid.client_name || "No client"} · {bid.location || "—"}
              {signoff?.summary && (
                <span data-testid="bid-round-info">
                  {" · "}Round {signoff.summary.round}
                  {signoff.summary.last_sent_at ? ` · last sent ${fmtDay(signoff.summary.last_sent_at)} at ${moneyCompact(signoff.summary.last_sent_total)}` : " · not sent yet"}
                </span>
              )}
            </div>
            {chromeOpen && bid.lead_context && (bid.lead_context.contact_name || bid.lead_context.source || bid.lead_context.notes || bid.lead_context.contact_email) && (
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500" data-testid="bid-lead-context">
                {bid.lead_context.contact_name && <span><span className="text-slate-400">Contact:</span> {bid.lead_context.contact_name}</span>}
                {bid.lead_context.contact_email && <span>{bid.lead_context.contact_email}</span>}
                {bid.lead_context.contact_phone && <span>{bid.lead_context.contact_phone}</span>}
                {bid.lead_context.source && <span className="px-1.5 py-0.5 rounded bg-[#003E87]/10 text-[#003E87] font-medium">{bid.lead_context.source}</span>}
                {bid.lead_context.notes && <span className="text-slate-400 italic truncate max-w-md" title={bid.lead_context.notes}>“{bid.lead_context.notes}”</span>}
              </div>
            )}
            {chromeOpen && !bid.read_only && (
              <KeyDatesStrip bid={bid} editable={!locked && p.canPrice} onSaved={(b) => setBid(b)} />
            )}
          </div>
          <div className="flex-1 min-w-[8px]" />
          <div className="flex items-center gap-x-6 gap-y-2 flex-wrap justify-end">
            {chromeOpen && <Metric label="Capacity" value={`${num(bid.capacity_mw)} MW`} />}
            {chromeOpen && <Metric label="$/Watt" value={bid.read_only ? perWatt(bid.stored_cost_per_watt) : (t ? perWatt(t.cost_per_watt) : "—")} />}
            {chromeOpen && <ScopeCompare bidId={bid.bid_id} compact />}
            <div className={`text-right rounded-md px-1.5 py-0.5 transition-colors duration-500 ${totalFlash ? "bg-[#F4C400]/40" : "bg-transparent"}`} data-testid="bid-total-block">
              <div className="text-[11px] uppercase tracking-wide text-slate-400">Total</div>
              {bid.read_only ? (
                <div className="text-lg font-semibold text-slate-900 font-mono tabular-nums">{money(bid.stored_total)}</div>
              ) : t && t.derivation ? (
                <ShowMath
                  testid="show-the-math-trigger-total-cost"
                  label="Total contract price"
                  value={money(t.total)}
                  tone="text-slate-900 text-lg font-semibold"
                  rows={[
                    { title: "Direct cost", text: t.derivation.direct_cost },
                    { title: "Contingency", text: t.derivation.contingency },
                    { title: "Markup", text: t.derivation.markup },
                    { title: "$/Watt", text: t.derivation.cost_per_watt },
                  ]}
                  summary={t.derivation.total}
                />
              ) : t ? (
                <div className="text-lg font-semibold text-slate-900 font-mono tabular-nums" title={t.award_note || ""}>{money(t.total)}</div>
              ) : <div className="text-lg font-semibold text-slate-300">—</div>}
              {t?.standalone_total != null && <div className="text-[11px] text-slate-500 font-mono" data-testid="bid-award-note">sites alone {money(t.standalone_total)} · portfolio credit −{money(t.portfolio_credit || 0)} · award price {money(t.total)}</div>}
            </div>
          <div className="flex items-center gap-2">
            <InBidSearch bidId={bid.bid_id} onSheet={(sid, term) => openSheetInTakeoff({ sheet_id: sid }, term)} onDoc={() => setTab("documents")} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="bid-export-menu-button">
                  <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Export <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuItem data-testid="bid-export-button" onClick={async () => {
                  try { const res = await api.get(`/bids/${bid.bid_id}/export`, { responseType: "blob" });
                    const url = URL.createObjectURL(res.data); const a = document.createElement("a");
                    a.href = url; a.download = `estimate_bid.xlsx`; a.click(); URL.revokeObjectURL(url);
                  } catch { toast.error("Could not export bid"); }
                }}>
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-[#003E87]" />
                  <div><div className="text-sm">Estimate workbook</div><div className="text-[11px] text-slate-400">Priced roll-up (.xlsx)</div></div>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="bid-estimate-export-button" onClick={async () => {
                  try { const res = await api.get(`/bids/${bid.bid_id}/estimate-export`, { responseType: "blob" });
                    const url = URL.createObjectURL(res.data); const a = document.createElement("a");
                    a.href = url; a.download = `estimate_estimate.xlsx`; a.click(); URL.revokeObjectURL(url);
                    toast.success("Full estimate exported — edit it and re-import to update this bid");
                  } catch { toast.error("Could not export estimate"); }
                }}>
                  <Layers className="w-4 h-4 mr-2 text-[#003E87]" />
                  <div><div className="text-sm">Full estimate (round-trip)</div><div className="text-[11px] text-slate-400">Edit &amp; re-import to update (.xlsx)</div></div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {!user?.features?.assistant_claude && <Button variant="outline" onClick={() => window.askSol?.(bid, `Give me a quick rundown of “${bid.name}” — status, price, margin, and what needs attention.`)} data-testid="bid-ask-sol-button">
              <Sun className="w-4 h-4 mr-1.5" /> Ask about this bid
            </Button>}
            <Button variant="outline" onClick={() => setFollowOpen(true)} data-testid="bid-followups-button" title="Schedule & track follow-ups">
              <ListChecks className="w-4 h-4 mr-1.5" /> Follow-ups
            </Button>
            {isRevision && (
              <Button variant="outline" onClick={repriceIndex} data-testid="reprice-index-button" title="Reprice all index-linked lines to today's commodity rates">
                <TrendingUp className="w-4 h-4 mr-1.5" /> Reprice to index
              </Button>
            )}
            {repricePreview && (
              <Dialog open onOpenChange={(o) => !o && setRepricePreview(null)}>
                <DialogContent className="max-w-lg" data-testid="reprice-preview-dialog">
                  <DialogHeader>
                    <DialogTitle>Reprice to current index rates</DialogTitle>
                    <DialogDescription>Review the market-driven changes before applying. Only index-linked lines move — everything else stays frozen.</DialogDescription>
                  </DialogHeader>
                  <div className="flex items-center justify-between px-1 pb-2 mb-1 border-b border-slate-100 text-sm" data-testid="reprice-total-impact">
                    <span className="text-slate-500">Total price impact</span>
                    <span className={`font-mono font-semibold ${(repricePreview.total || 0) >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{(repricePreview.total || 0) >= 0 ? "+" : "−"}{money(Math.abs(repricePreview.total || 0))}</span>
                  </div>
                  <div className="max-h-[50vh] overflow-auto divide-y divide-slate-100">
                    {repricePreview.changes.map((c) => {
                      const from = Number(c.from) || 0, to = Number(c.to) || 0;
                      const up = to >= from;
                      const fmt = (v) => `$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 4 })}`;
                      const pct = from ? ((to - from) / from) * 100 : 0;
                      return (
                        <div key={c.line_id} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`reprice-row-${c.line_id}`}>
                          <div className="min-w-0"><div className="truncate text-slate-800">{c.name || "Line"}</div><div className="text-[11px] text-slate-400">{c.index}</div></div>
                          <div className="flex items-center gap-2 font-mono text-xs shrink-0">
                            <span className="text-slate-400 line-through">{fmt(from)}</span>
                            <span className={up ? "text-emerald-700" : "text-rose-600"}>{fmt(to)}</span>
                            <span className={`text-[10px] ${up ? "text-emerald-600" : "text-rose-500"}`}>{up ? "+" : ""}{pct.toFixed(1)}%</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setRepricePreview(null)}>Cancel</Button>
                    <Button onClick={applyReprice} className="bg-[#003E87] hover:bg-[#00306a]" data-testid="reprice-apply-button">Apply {repricePreview.changes.length} change{repricePreview.changes.length === 1 ? "" : "s"}</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            <Dialog open={followOpen} onOpenChange={setFollowOpen}>
              <DialogContent data-testid="bid-followups-dialog">
                <DialogHeader>
                  <DialogTitle>Follow-ups — {bid.name}</DialogTitle>
                  <DialogDescription>Schedule the next step and keep this pursuit moving. Overdue follow-ups surface on Today.</DialogDescription>
                </DialogHeader>
                <ActivityPanel entityType="bid" entityId={bid.bid_id} readOnly={locked} />
              </DialogContent>
            </Dialog>
            <Dialog open={tplOpen} onOpenChange={setTplOpen}>
              <DialogContent data-testid="save-template-dialog">
                <DialogHeader>
                  <DialogTitle>Save as project template</DialogTitle>
                  <DialogDescription>Reuse this whole project later — scope, crews, General Conditions & commercial setup carry over; a new bid starts at $0 with quantities zeroed.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-1">
                  <label className="block">
                    <span className="text-xs text-slate-500">Template name</span>
                    <input value={tplName} onChange={(e) => setTplName(e.target.value)} data-testid="save-template-name"
                      className="w-full mt-1 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:border-[#003E87] outline-none" />
                  </label>
                  <div>
                    <span className="text-xs text-slate-500">Category</span>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {[["mechanical", "Mechanical"], ["electrical", "Electrical"], ["civil", "Civil"], ["full_epc", "Full EPC"], ["other", "Other"]].map(([k, lab]) => (
                        <button key={k} onClick={() => setTplCat(k)} data-testid={`save-template-cat-${k}`}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${tplCat === k ? "bg-[#003E87] text-white border-[#003E87]" : "text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{lab}</button>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setTplOpen(false)}>Cancel</Button>
                  <Button onClick={submitTemplate} className="bg-[#003E87] hover:bg-[#00306a]" data-testid="save-template-confirm">Save template</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <div className="relative">
              <Button variant="outline" onClick={async () => {
                const n = !showRevs; setShowRevs(n); setShowHistory(false); setRevDiff(null); setSelRev(null); setCompareTo("current");
                if (n) { try { const r = await api.get(`/bids/${bid.bid_id}/revisions`); setRevs(r.data || []); } catch { setRevs([]); } }
              }} data-testid="bid-revisions-button" title="Compare revisions">
                <GitBranch className="w-4 h-4" />
              </Button>
              {showRevs && (
                <div className="absolute right-0 top-full mt-1 w-96 max-h-[28rem] overflow-auto bg-white rounded-lg shadow-xl border border-slate-200 z-50 p-2" data-testid="bid-revisions-panel">
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 px-2 py-1">Revisions — pick one, then compare it to</div>
                  <div className="px-2 pb-2">
                    <select value={compareTo} onChange={(e) => { setCompareTo(e.target.value); if (selRev) fetchRevDiff(selRev, e.target.value); }} className="w-full text-xs border border-slate-200 rounded px-2 py-1 bg-white" data-testid="revision-compare-to">
                      <option value="current">Compare to: Current (working)</option>
                      {revs.filter((rv) => rv.revision_id !== selRev).map((rv) => <option key={rv.revision_id} value={rv.revision_id}>Compare to: Rev {rv.revision}</option>)}
                    </select>
                  </div>
                  {revs.length === 0 ? (
                    <div className="text-xs text-slate-400 px-2 py-3">No sent revisions yet.</div>
                  ) : revs.map((rv) => (
                    <button key={rv.revision_id} onClick={() => {
                      setSelRev(rv.revision_id);
                      let to = compareTo;
                      if (compareTo === rv.revision_id) { to = "current"; setCompareTo("current"); }
                      fetchRevDiff(rv.revision_id, to);
                    }} className={`w-full text-left flex items-center justify-between px-2 py-1.5 rounded ${selRev === rv.revision_id ? "bg-[#F4C400]/15" : "hover:bg-slate-50"}`} data-testid={`revision-${rv.revision}`}>
                      <span className="text-xs text-slate-700">Rev {rv.revision} · {rv.sent_by}</span>
                      <span className="text-[11px] text-slate-400 font-mono">{fmtMoney(rv.total)}</span>
                    </button>
                  ))}
                  {revDiff && !revDiff.loading && (
                    <div className="mt-2 border-t border-slate-100 pt-2 px-2" data-testid="revision-diff">
                      <div className="flex items-center justify-between text-[11px] mb-1">
                        <span className="text-slate-500">{revDiff.a_label} → {revDiff.b_label}</span>
                        <span className="font-mono text-slate-600">{fmtMoney(revDiff.old_total)} → {fmtMoney(revDiff.new_total)}</span>
                      </div>
                      <div className="flex gap-3 text-[11px] mb-1">
                        <span className="text-emerald-600">+{revDiff.added.length} added</span>
                        <span className="text-[#8a6d00]">~{revDiff.changed.length} changed</span>
                        <span className="text-rose-600">−{revDiff.removed.length} removed</span>
                      </div>
                      <ul className="max-h-40 overflow-auto text-[11px] text-slate-500 space-y-0.5">
                        {revDiff.added.map((n, i) => <li key={`a${i}`} className="text-emerald-700">+ {n}</li>)}
                        {revDiff.changed.map((c, i) => <li key={`c${i}`} className="text-[#8a6d00]">~ {c.name} — {c.fields.map((f) => f.replace(/_override|_pct/g, "").replace(/_/g, " ")).join(", ")}{c.quantity && c.quantity[0] !== c.quantity[1] ? ` (qty ${c.quantity[0] ?? "—"}→${c.quantity[1] ?? "—"})` : ""}</li>)}
                        {revDiff.removed.map((n, i) => <li key={`r${i}`} className="text-rose-600">− {n}</li>)}
                      </ul>
                      <button onClick={async () => {
                        try { const params = { revision_id: revDiff.revision_id }; if (revDiff.to_id && revDiff.to_id !== "current") params.to_revision_id = revDiff.to_id;
                          const res = await api.get(`/bids/${bid.bid_id}/revision-compare`, { params, responseType: "blob" });
                          const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `estimate_compare.xlsx`; a.click(); URL.revokeObjectURL(url);
                        } catch { toast.error("Could not export comparison"); }
                      }} className="mt-2 w-full text-[11px] text-[#003E87] hover:underline flex items-center justify-center gap-1" data-testid="revision-compare-export">
                        <FileSpreadsheet className="w-3 h-3" /> Export comparison to Excel
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="relative">
              <Button variant="outline" onClick={() => setShowHistory((v) => !v)} data-testid="bid-io-history-button" title="Import / export history">
                <History className="w-4 h-4" />
              </Button>
              {showHistory && (
                <div className="absolute right-0 top-full mt-1 w-80 max-h-80 overflow-auto bg-white rounded-lg shadow-xl border border-slate-200 z-50 p-2" data-testid="bid-io-history-panel">
                  <div className="flex items-center justify-between px-2 py-1">
                    <div className="text-[11px] uppercase tracking-wide text-slate-400">Estimate I/O history</div>
                    <div className="flex items-center gap-1">
                      {["all", "import", "export"].map((f) => (
                        <button key={f} onClick={() => setHistFilter(f)} data-testid={`io-filter-${f}`}
                          className={`text-[10px] px-1.5 py-0.5 rounded ${histFilter === f ? "bg-[#003E87] text-white" : "text-slate-500 hover:bg-slate-100"}`}>{f}</button>
                      ))}
                    </div>
                  </div>
                  {(() => {
                    const items = [...(bid.io_history || [])].reverse().filter((h) => histFilter === "all" || (histFilter === "import" ? h.action.includes("import") : h.action === "export"));
                    return items.length === 0 ? (
                      <div className="text-xs text-slate-400 px-2 py-3">No {histFilter === "all" ? "imports or exports" : histFilter + "s"} yet.</div>
                    ) : items.map((h, i) => (
                      <div key={i} className="flex items-start gap-2 px-2 py-1.5 border-b border-slate-50 last:border-0" data-testid={`io-history-${i}`}>
                        {h.action === "export" ? <Layers className="w-3.5 h-3.5 text-[#003E87] mt-0.5 shrink-0" /> : <ArrowDownToLine className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />}
                        <div className="min-w-0">
                          <div className="text-xs text-slate-700 capitalize">{h.action.replace("-", " ")}{h.detail ? ` · ${h.detail}` : ""}</div>
                          <div className="text-[11px] text-slate-400">{h.by} · {new Date(h.at).toLocaleString()}</div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
            {p.canPrice && (
              <Button variant="outline" onClick={openSaveTemplate} data-testid="bid-save-template-button">
                <Copy className="w-4 h-4 mr-1.5" /> Save as template
              </Button>
            )}
            <ProjectChatButton bidId={bidId} />
            {locked ? (
              p.canPrice && (
                <Button variant="outline" onClick={() => createVersion("revision")} data-testid="bid-revise-button">
                  <GitBranch className="w-4 h-4 mr-1.5" /> New revision
                </Button>
              )
            ) : (
              p.canSend && !bid.read_only && !sendLock && (
                <Button
                  onClick={() => sendBid()}
                  disabled={bid.status === "lead"}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="bid-send-button"
                  title={needsDiscipline ? "Set the project discipline and scope completeness on Estimating → Lead & details before sending." : "Send this bid to the client"}
                >
                  <Send className="w-4 h-4 mr-1.5" /> Send bid
                </Button>
              )
            )}
          </div>
          </div>
        </div>

        {/* Tabs — grouped by estimating stage for clarity */}
        <div className="flex items-end px-5">
          <div className="flex items-end gap-1 flex-nowrap overflow-x-auto min-w-0 flex-1" style={{ scrollbarWidth: "thin" }} data-testid="bid-tab-groups">
          {TAB_GROUPS.map((grp, gi) => (
            <div key={grp.key} className="flex items-end shrink-0">
              {gi > 0 && <div className="w-px self-stretch bg-slate-200 mb-2 mx-2" />}
              <div className="flex flex-col">
                <span className="px-2 pt-1 text-[9px] font-semibold uppercase tracking-widest text-slate-400 whitespace-nowrap">{grp.label}</span>
                <div className="flex items-center">
                  {grp.keys.map((k) => {
                    const tb = TABS.find((t) => t.key === k);
                    if (!tb) return null;
                    return (
                      <button key={tb.key} data-testid={tb.testid} onClick={() => setTab(tb.key)}
                        className={`px-2.5 py-2 text-[13px] border-b-2 transition-colors whitespace-nowrap ${tab === tb.key ? "border-[#003E87] text-[#003E87] font-semibold" : "border-transparent text-slate-600 hover:text-slate-900"}`}>
                        {tb.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
          </div>
          <div className="flex items-center gap-2 pb-1 shrink-0 pl-3 ml-1 border-l border-slate-100">
            <button onClick={toggleChrome} data-testid="bid-chrome-toggle" title="Collapse to a thin strip"
              className="flex items-center gap-1 h-7 px-2 text-[11px] text-slate-500 hover:text-slate-800 rounded-md hover:bg-slate-100 whitespace-nowrap">
              <ChevronUp className="w-3.5 h-3.5" /> Hide details
            </button>
          </div>
        </div>
      </div>
      )}

      {locked && (
        <div className="shrink-0 bg-slate-900 text-white text-xs px-5 py-2 flex items-center gap-2" data-testid="bid-locked-banner">
          <Lock className="w-3.5 h-3.5" />
          This bid was sent {bid.sent_at ? `on ${new Date(bid.sent_at).toLocaleDateString()}` : ""} and is stored exactly as sent. It is never recalculated. Open a new revision to change it.
        </div>
      )}

      {bid.read_only && !locked && (
        <div className="shrink-0 bg-slate-100 border-b border-slate-200 text-slate-600 text-xs px-5 py-2 flex items-center gap-2" data-testid="bid-readonly-banner">
          <Lock className="w-3.5 h-3.5" />
          Imported from your previous system — stored exactly as priced/sent. Read-only, and never recomputed here. Figures shown are the stored numbers.
        </div>
      )}

      {chromeOpen && !bid.imported && !cmdtyDismissed && commodityAlerts.length > 0 && (
        <div className="shrink-0 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs px-5 py-2 flex items-center gap-2" data-testid="commodity-alert-banner">
          <TrendingUp className="w-3.5 h-3.5 shrink-0" />
          <span className="flex-1">
            {commodityAlerts.map((a) => `${a.name} ${a.drift >= 0 ? "up" : "down"} ${Math.abs(Math.round(a.drift * 100))}%`).join(" · ")} since this bid was priced. Create a revision and use <b>Reprice to index</b> to capture current rates.
          </span>
          <button onClick={() => setCmdtyDismissed(true)} className="text-amber-600 hover:text-amber-900 font-medium shrink-0" data-testid="commodity-alert-dismiss">Dismiss</button>
        </div>
      )}

      {nda && !bid.read_only && tab !== "takeoff" && <div className="mx-6 mt-3"><NdaBanner summary={nda} clientName={nda.client_name} what={nda.client_name} testid="bid-nda-banner" /></div>}
      {optionId && bid._option && (
        <div className="mx-6 mt-3 rounded-lg border border-[#003E87]/30 bg-[#003E87]/5 px-4 py-2 flex items-center gap-3 flex-wrap text-sm" data-testid="option-mode-banner">
          <span className="font-semibold text-[#003E87]">Editing option “{bid._option.name}”</span>
          <span className="text-slate-600">Fields you change here become this option's own{(bid._option.owns || []).length ? ` (owns: ${bid._option.owns.join(", ")})` : " — it currently shares everything with the base"}. Sending and sign-off happen on the base.</span>
          <button onClick={() => navigate(`/bids/${bidId}?tab=options`)} data-testid="option-mode-exit" className="ml-auto text-xs px-2 py-1 rounded-lg border border-[#003E87]/40 text-[#003E87] hover:bg-white">Back to base</button>
        </div>
      )}
      <NdaOverrideDialog gate={ndaGate} onClose={() => setNdaGate(null)} onRetry={(reason) => ndaGate?.retry?.(reason)} />
      <DraftsCard bidId={bidId} variant="strip" />
      {!bid.read_only && tab !== "takeoff" && !optionId && (
        <>
        <ApprovalBanner bidId={bidId} signoff={signoff} lineItems={(bid.scope_lines || []).map((l) => l.name).filter(Boolean)} onChange={() => { load(); loadSignoff(); }} />
        </>
      )}
      {/* Body */}
      {chromeOpen && !bid.imported && checklist && <BidChecklist steps={checklist.steps} nextKey={checklist.next} complete={checklist.complete} onGo={setTab} />}
      <div className={`flex-1 min-h-0 ${tab === "takeoff" ? "overflow-hidden" : "overflow-auto"} ${tab === "documents" ? "max-md:min-h-[80vh]" : ""}`}>
        {viewingId && viewingId !== activeVersionId && viewSnap ? (
          <VersionView snapshot={viewSnap} activeLabel={activeLabel}
            onBackToCurrent={() => selectVersion({ version_id: activeVersionId })} />
        ) : (
          <>
        {tab === "context" && (
          <ContextTab bidId={bidId} autoReply={sp.get("reply") === "1"} />
        )}
        {tab === "documents" && (
          <DocumentsTab bid={bid} readOnly={locked || !p.canTakeoff} canDelete={p.canDelete} onOpenSheet={openSheetInTakeoff} />
        )}
        {tab === "takeoff" && (
          <div ref={fullRef} id="takeoff-fs-root"
            data-testid={takeoffFull ? "takeoff-fullscreen" : "takeoff-tab-root"}
            className={takeoffFull ? "fixed inset-0 z-[2147483647] bg-white flex flex-col" : "h-full flex flex-col"}>
            <div className="flex-1 min-h-0">{takeoffTabEl}</div>
          </div>
        )}
        {tab === "estimating" && (
          <EstimatingTab bid={bid} setBid={setBid} computed={computed} taskTypes={taskTypes} crews={crews}
            updateBid={updateBid} perms={p} locked={locked} load={load}
            pendingMeasure={pendingMeasure} setPendingMeasure={setPendingMeasure} />
        )}
        {tab === "leveling" && <LevelingTab bid={bid} readOnly={locked || !p.canPrice} />}
        {tab === "proposal" && <ProposalTab bid={bid} perms={p} locked={locked} />}
        {tab === "location" && <LocationPanel bid={bid} updateBid={updateBid} computed={computed} readOnly={locked} />}
        {tab === "commercial_terms" && (
          <CommercialTermsTab bid={bid} computed={computed} updateBid={updateBid} readOnly={locked || !p.canSetMargin} />
        )}
        {tab === "rollup" && (
          <div data-testid="rollup-tab">
            <div className="px-6 pt-5 pb-1"><h2 className="text-base font-semibold text-slate-900">Rollup</h2><p className="text-xs text-slate-500">The full cost roll-up and exports. Margins &amp; terms are set on the <b>Commercial Terms</b> tab — this page is the read-only summary of everything flowing into the price.</p></div>
            <PricingMath bid={bid} computed={computed} updateBid={updateBid} perms={p} readOnly={locked} termsLocked />
          </div>
        )}
        {tab === "schedule" && <ScheduleTab bid={bid} computed={computed} updateBid={updateBid} readOnly={locked} />}
        {tab === "management" && <ManagementTab bid={bid} computed={computed} updateBid={updateBid} readOnly={locked} />}
        {tab === "conditions" && <GeneralConditions bid={bid} computed={computed} updateBid={updateBid} readOnly={locked} />}
        {tab === "mobilization" && <MobilizationTab bid={bid} computed={computed} updateBid={updateBid} readOnly={locked} />}
        {tab === "allowances" && <AllowancesTab bid={bid} computed={computed} updateBid={updateBid} readOnly={locked} />}
        {tab === "options" && <OptionsTab bid={bid} computed={computed} updateBid={updateBid} readOnly={locked || !p.canPrice} optionId={optionId} />}
        {tab === "contract" && <ContractTab bid={bid} computed={computed} updateBid={updateBid} readOnly={locked} />}
        <NextCue tab={tab} setTab={setTab} />
          </>
        )}
      </div>

      {/* Estimate version tabs (Procore-style) */}
      <VersionTabs versions={versions} activeVersionId={activeVersionId} viewingId={viewingId}
        locked={locked} canEdit={p.canPrice} bidId={bid.bid_id} whyNote={whyNote} onWhyNote={setWhyNote}
        onSelect={selectVersion} onCreate={createVersion} onDelete={deleteVersion} />
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="text-right">
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-sm font-mono font-medium text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

// Consistent "obvious next step" cue at the foot of every workspace tab.
function NextCue({ tab, setTab }) {
  const idx = TABS.findIndex((t) => t.key === tab);
  const next = idx >= 0 && idx < TABS.length - 1 ? TABS[idx + 1] : null;
  if (!next) return null;
  return (
    <div className="px-6 py-5 flex items-center justify-end border-t border-slate-100 mt-2" data-testid="tab-next-cue">
      <span className="text-xs text-slate-400 mr-3">Next step</span>
      <button onClick={() => setTab(next.key)} data-testid="tab-next-cue-btn"
        className="inline-flex items-center gap-2 rounded-full bg-[#003E87] text-white text-sm font-medium pl-4 pr-3 py-2 hover:bg-[#00306a] transition-colors shadow-sm">
        {next.label} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}
